<script setup>
import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import { Link, usePage } from "@inertiajs/vue3";
import { computed } from "vue";

const page = usePage();
const supportEmail = computed(() => page.props.supportEmail || 'contact@qgmecs.com');

const navigationLinks = [
    { label: "Accueil", url: "/" },
    { label: "Témoignages", url: "/#reviews" },
    { label: "Événements", url: route('events.index') },
    { label: "Offres", url: "/#offres" },
];

const legalLinks = [
    { label: "Mentions légales", url: route('mentions')},
    { label: "CGU", url: route('terms') },
    { label: "Politique de confidentialité", url: route('rgpd') },
];

const socialLinks = [
    { name: 'Instagram', url: '#', icon: 'instagram' },
    { name: 'Facebook', url: '#', icon: 'facebook' },
    { name: 'Twitter', url: '#', icon: 'twitter' },
    { name: 'TikTok', url: '#', icon: 'tiktok' },
];
</script>
<template>
    <footer style="background-color: #0A1929;">
        <!-- Main Footer Content -->
        <div class="container mx-auto py-8 px-4">
            <!-- Top Section: Email, Logo, Social -->
            <div class="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
                <!-- Email -->
                <div class="flex items-center gap-2 text-white text-sm">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    <span>{{ supportEmail }}</span>
                </div>

                <!-- Logo -->
                <div>
                    <ApplicationLogo class="h-16 w-auto" dark />
                </div>

                <!-- Social Links -->
                <div class="flex gap-3">
                    <a
                        v-for="social in socialLinks"
                        :key="social.name"
                        :href="social.url"
                        target="_blank"
                        class="text-white hover:text-primary transition-colors"
                        :aria-label="social.name"
                    >
                        <svg v-if="social.icon === 'instagram'" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                        <svg v-else-if="social.icon === 'facebook'" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        <svg v-else-if="social.icon === 'twitter'" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        <svg v-else-if="social.icon === 'tiktok'" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                        </svg>
                    </a>
                </div>
            </div>

            <!-- Middle Section: Navigation Links -->
            <div class="border-t border-white/10 py-6">
                <div class="flex flex-wrap justify-center gap-x-8 gap-y-3">
                    <Link
                        v-for="link in navigationLinks"
                        :key="link.label"
                        :href="link.url"
                        class="text-sm text-white hover:text-primary transition-colors"
                    >
                        {{ link.label }}
                    </Link>
                </div>
            </div>

            <!-- Bottom Section: Copyright & Legal -->
            <div class="border-t border-white/10 pt-6">
                <div class="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
                    <!-- Copyright -->
                    <p>
                        © Copyright QGMecs {{ new Date().getFullYear() }} All Rights Reserved
                    </p>

                    <!-- Legal Links -->
                    <div class="flex flex-wrap gap-6 justify-center">
                        <Link
                            v-for="link in legalLinks"
                            :key="link.label"
                            :href="link.url"
                            class="hover:text-white transition-colors"
                        >
                            {{ link.label }}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </footer>
</template>
